import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { PaperClipIcon, PaperAirplaneIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import ConfirmDialog from '../MasterAdmin/ui/ConfirmDialog';
import { getApiErrorMessage } from '../../utils/apiErrorMessage';
import WhatsAppDevicePreview from './WhatsAppDevicePreview';
import { devicePreviewFromTemplate } from './whatsappTemplatePreview';
import WhatsAppPanelShell from './WhatsAppPanelShell';
import WhatsAppSendRecipientPicker from './WhatsAppSendRecipientPicker';
import WhatsAppSendVariableEditor from './WhatsAppSendVariableEditor';
import WhatsAppSendButtonLinkEditor, {
  applySendButtonPreview,
  defaultSendButtonSlots,
  sendButtonSlotsToButtonValues,
  type SendButtonLinkSlot,
} from './WhatsAppSendButtonLinkEditor';
import WhatsAppTemplateSearchSelect from './WhatsAppTemplateSearchSelect';
import { applyDoctorHonorificDedupeToBodyValues } from './whatsappDoctorHonorific';
import {
  buildPreviewMessage,
  buildWhatsAppAttachmentMediaKey,
  formatFileSize,
  getAttachmentLimit,
  headerFormatAcceptAttribute,
  inferVariableLabels,
  isMediaHeaderFormat,
} from './whatsappPreview';
import {
  defaultSendSlots,
  getSendDeepLinkContextFields,
  resolveSendPreviewValues,
  sendSlotToBodyValue,
  sendSlotsAreValid,
  type SendTemplateVariableSlot,
} from './whatsappSendRecipientFields';
import { formatDisplayPhone } from './whatsappUi';
import type { WhatsAppSendRecipient, WhatsAppSendTemplateLocationState } from '../../models/MasterAdmin/whatsappHub';
import { WHATSAPP_BASE_PATH } from '../../models/MasterAdmin/whatsappHub';
import type { WhatsAppTemplate } from '../../models/MasterAdmin/whatsappCampaign';
import type { DeepLinkCampaign } from '../../models/MasterAdmin/deepLink';
import {
  fetchApprovedWhatsAppTemplates,
  fetchRcsStatus,
  sendInboxBulkMessage,
  sendInboxMessage,
  sendRcsBulkMessage,
  sendRcsMessage,
} from '../../services/MasterAdmin/whatsappCampaignService';
import { fetchDeepLinkCampaigns } from '../../services/MasterAdmin/deepLinkService';
import { fetchPublicMediaByKey, uploadMediaAsset } from '../../services/MasterAdmin/mediaAssetService';
import WhatsAppChannelToggle, { type MessageChannel } from './WhatsAppChannelToggle';

const WhatsAppSendTemplatePanel: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [templates, setTemplates] = useState<WhatsAppTemplate[]>([]);
  const [recipients, setRecipients] = useState<WhatsAppSendRecipient[]>([]);
  const [templateName, setTemplateName] = useState('');
  const [languageCode, setLanguageCode] = useState('en');
  const [variableSlots, setVariableSlots] = useState<SendTemplateVariableSlot[]>([]);
  const [buttonSlots, setButtonSlots] = useState<SendButtonLinkSlot[]>([]);
  const [headerMediaUrl, setHeaderMediaUrl] = useState<string | null>(null);
  const [headerMimeType, setHeaderMimeType] = useState<string | null>(null);
  const [attachmentName, setAttachmentName] = useState<string | null>(null);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const [step, setStep] = useState<'compose' | 'confirm'>('compose');
  const [sizeError, setSizeError] = useState<{ name: string; size: number; limit: number } | null>(null);
  const [sending, setSending] = useState(false);
  const [deepLinkCampaigns, setDeepLinkCampaigns] = useState<DeepLinkCampaign[]>([]);
  const [channel, setChannel] = useState<MessageChannel>('whatsapp');
  const [rcsStatus, setRcsStatus] = useState<Awaited<ReturnType<typeof fetchRcsStatus>> | null>(null);
  const [rcsTemplateName, setRcsTemplateName] = useState('');
  const [rcsBodyValues, setRcsBodyValues] = useState<string[]>([]);

  const campaignsById = useMemo(
    () => new Map(deepLinkCampaigns.map((c) => [c.id, c])),
    [deepLinkCampaigns]
  );

  const sendContextFields = useMemo(() => getSendDeepLinkContextFields(recipients), [recipients]);

  const selectedTemplate = useMemo(
    () => templates.find((t) => t.name === templateName),
    [templates, templateName]
  );

  const variableLabels = useMemo(() => {
    const count = Math.max(selectedTemplate?.variableCount ?? 0, variableSlots.length);
    return inferVariableLabels(selectedTemplate?.bodyPreview || '', count);
  }, [selectedTemplate, variableSlots.length]);

  const bodyValues = useMemo(
    () => variableSlots.map(sendSlotToBodyValue),
    [variableSlots]
  );

  const previewBodyValues = useMemo(
    () => resolveSendPreviewValues(variableSlots, recipients[0], campaignsById),
    [variableSlots, recipients, campaignsById]
  );

  const previewMessage = useMemo(
    () => buildPreviewMessage(selectedTemplate?.bodyPreview || '', previewBodyValues),
    [selectedTemplate, previewBodyValues]
  );

  const devicePreviewProps = useMemo(() => {
    if (!selectedTemplate) {
      return {
        message: previewMessage || 'Select a template to preview your message',
        headerText: null as string | null,
        footer: null as string | null,
        buttons: [] as import('../../models/MasterAdmin/whatsappCampaign').WhatsAppTemplateButton[],
        headerMediaUrl,
        headerMimeType,
      };
    }
    const preview = devicePreviewFromTemplate(selectedTemplate, { headerMediaUrl, headerMimeType });
    return {
      ...preview,
      message: previewMessage || 'Select a template to preview your message',
      buttons: applySendButtonPreview(preview.buttons, buttonSlots, campaignsById),
    };
  }, [selectedTemplate, previewMessage, headerMediaUrl, headerMimeType, buttonSlots, campaignsById]);

  const supportsMediaHeader = isMediaHeaderFormat(selectedTemplate?.headerFormat);
  const previewContactName = recipients[0]?.displayName || 'Contact';

  const selectedRcsTemplate = useMemo(
    () => (rcsStatus?.templates || []).find((t) => t.name === rcsTemplateName) || null,
    [rcsStatus, rcsTemplateName]
  );

  useEffect(() => {
    fetchApprovedWhatsAppTemplates().then((r) => setTemplates(r.data || []));
    fetchDeepLinkCampaigns()
      .then(setDeepLinkCampaigns)
      .catch(() => setDeepLinkCampaigns([]));
    fetchRcsStatus()
      .then((data) => {
        setRcsStatus(data);
        setRcsTemplateName((prev) => prev || data.templateName || data.templates?.[0]?.name || '');
      })
      .catch(() => setRcsStatus(null));
  }, []);

  useEffect(() => {
    if (!supportsMediaHeader) {
      setHeaderMediaUrl(null);
      setHeaderMimeType(null);
      setAttachmentName(null);
    }
  }, [supportsMediaHeader]);

  useEffect(() => {
    const state = location.state as WhatsAppSendTemplateLocationState | null;
    if (state?.recipients?.length) {
      setRecipients(state.recipients);
      navigate(location.pathname, { replace: true, state: null });
    }
    // Pre-fill recipients once when opened from inbox or contacts
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTemplateChange = (name: string) => {
    setTemplateName(name);
    const tpl = templates.find((t) => t.name === name);
    setLanguageCode(tpl?.languageCode || 'en');
    const count = tpl?.variableCount ?? 0;
    setVariableSlots(defaultSendSlots(count, tpl?.defaultVariableMappings));
    setButtonSlots(defaultSendButtonSlots(tpl));
  };

  const handleAttachment = async (file: File | null) => {
    if (!file) return;
    const limit = getAttachmentLimit(file.type || '');
    if (file.size > limit) {
      setSizeError({ name: file.name, size: file.size, limit });
      return;
    }
    setUploadingAttachment(true);
    try {
      const key = buildWhatsAppAttachmentMediaKey(file);
      await uploadMediaAsset({ file, key, name: file.name });
      const pub = await fetchPublicMediaByKey(key);
      setHeaderMediaUrl(pub.fileUrl);
      setHeaderMimeType(pub.mimeType || file.type);
      setAttachmentName(file.name);
      toast.success('Attachment ready');
    } catch (e: unknown) {
      toast.error(getApiErrorMessage(e, 'Upload failed'));
    } finally {
      setUploadingAttachment(false);
    }
  };

  const handleRcsTemplateChange = (name: string) => {
    setRcsTemplateName(name);
    const tpl = (rcsStatus?.templates || []).find((t) => t.name === name);
    const count = Math.max(0, Number(tpl?.variableCount) || 0);
    setRcsBodyValues(Array.from({ length: count }, () => ''));
  };

  const variablesValid = variableSlots.length === 0 || sendSlotsAreValid(variableSlots);
  const rcsVariablesValid = rcsBodyValues.every((v) => String(v || '').trim().length > 0) || rcsBodyValues.length === 0;

  const canProceed =
    channel === 'rcs'
      ? recipients.length > 0 &&
        Boolean(rcsTemplateName) &&
        Boolean(rcsStatus?.enabled) &&
        rcsVariablesValid
      : recipients.length > 0 && Boolean(templateName) && !uploadingAttachment && variablesValid;

  const proceedHint = useMemo(() => {
    if (channel === 'rcs') {
      if (!rcsStatus?.enabled) return 'RCS is disabled. Set INTERAKT_RCS_ENABLED=true after connecting RCS in Interakt.';
      if (recipients.length === 0) return 'Select at least one recipient to continue';
      if (!rcsTemplateName) return 'Select or enter an RCS template code name';
      if (!rcsVariablesValid) return 'Fill all RCS template variables';
      return null;
    }
    if (uploadingAttachment) return 'Wait for attachment upload to finish';
    if (recipients.length === 0) return 'Select at least one recipient to continue';
    if (!templateName) return 'Select a template to continue';
    if (!variablesValid) return 'Fill all template variables — pick a notification field or enter static text';
    return null;
  }, [
    channel,
    rcsStatus?.enabled,
    recipients.length,
    rcsTemplateName,
    rcsVariablesValid,
    uploadingAttachment,
    templateName,
    variablesValid,
  ]);

  const handleConfirmSend = async () => {
    setSending(true);
    try {
      if (channel === 'rcs') {
        const rcsPayload = {
          templateName: rcsTemplateName,
          languageCode: selectedRcsTemplate?.languageCode || 'en',
          bodyValues: rcsBodyValues,
        };
        if (recipients.length === 1) {
          const r = recipients[0];
          await sendRcsMessage({
            countryCode: r.countryCode,
            phoneNumber: r.phoneNumber,
            contactName: r.displayName,
            recipientType: r.recipientType,
            recipientId: r.recipientId,
            ...rcsPayload,
          });
        } else {
          await sendRcsBulkMessage({
            recipients: recipients.map((r) => ({
              countryCode: r.countryCode,
              phoneNumber: r.phoneNumber,
              contactName: r.displayName,
              displayName: r.displayName,
              recipientType: r.recipientType,
              recipientId: r.recipientId,
            })),
            ...rcsPayload,
          });
        }
        toast.success(
          recipients.length === 1 ? 'RCS message sent' : `RCS sent to ${recipients.length} contacts`
        );
        navigate(WHATSAPP_BASE_PATH);
        return;
      }

      const cleanBodyValues = applyDoctorHonorificDedupeToBodyValues({
        bodyPreview: selectedTemplate?.bodyPreview,
        slots: variableSlots,
        values: bodyValues,
      });

      const sendPayload = {
        templateName,
        languageCode,
        bodyValues: cleanBodyValues,
        headerMediaUrl: headerMediaUrl || undefined,
        headerMimeType: headerMimeType || undefined,
        headerFileName: attachmentName || undefined,
        buttonValues: sendButtonSlotsToButtonValues(buttonSlots, selectedTemplate),
      };

      if (recipients.length === 1) {
        const r = recipients[0];
        await sendInboxMessage({
          countryCode: r.countryCode,
          phoneNumber: r.phoneNumber,
          contactName: r.displayName,
          email: r.email,
          city: r.city,
          platform: r.platform,
          vendorKind: r.vendorKind,
          recipientType: r.recipientType,
          recipientId: r.recipientId,
          tags: r.tags,
          ...sendPayload,
        });
      } else {
        await sendInboxBulkMessage({
          recipients: recipients.map((r) => ({
            countryCode: r.countryCode,
            phoneNumber: r.phoneNumber,
            contactName: r.displayName,
            displayName: r.displayName,
            email: r.email,
            city: r.city,
            platform: r.platform,
            vendorKind: r.vendorKind,
            recipientType: r.recipientType,
            recipientId: r.recipientId,
            tags: r.tags,
          })),
          ...sendPayload,
        });
      }

      toast.success(
        recipients.length === 1 ? 'Message sent' : `Sent to ${recipients.length} contacts`
      );
      navigate(WHATSAPP_BASE_PATH);
    } catch (e: unknown) {
      toast.error(getApiErrorMessage(e, 'Send failed'));
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <WhatsAppPanelShell
        title={channel === 'rcs' ? 'Send RCS template' : 'Send WhatsApp template'}
        description={
          channel === 'rcs'
            ? 'Pick contacts, choose an Interakt RCS template, fill variables, then send via RCS'
            : 'Pick contacts or platform users, choose template, map notification fields and button links, then send'
        }
        actions={
          <>
            <WhatsAppChannelToggle value={channel} onChange={setChannel} disabled={sending} />
            <button
              type="button"
              disabled={sending}
              onClick={() => navigate(WHATSAPP_BASE_PATH)}
              className="rounded-lg border border-gray-200 px-4 py-2 text-sm dark:border-gray-600"
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={step === 'confirm' ? sending : !canProceed}
              onClick={() => (step === 'confirm' ? void handleConfirmSend() : setStep('confirm'))}
              className={`inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold text-white disabled:opacity-50 ${
                channel === 'rcs' ? 'bg-violet-600 hover:bg-violet-700' : 'bg-[#25D366]'
              }`}
            >
              <PaperAirplaneIcon className="h-4 w-4" />
              {step === 'confirm' ? (sending ? 'Sending...' : 'Send') : `Send (${recipients.length})`}
            </button>
          </>
        }
      >
        <div className="-m-4 flex min-h-[min(720px,calc(100vh-12rem))] flex-col sm:-m-6">
          <div className="grid min-h-0 flex-1 gap-0 overflow-hidden lg:grid-cols-[1.1fr_0.9fr]">
            <div className="space-y-4 overflow-y-auto border-b border-gray-200 p-4 sm:p-6 lg:border-b-0 lg:border-r dark:border-gray-700">
              {step === 'confirm' && (
                <div className="rounded-xl border border-[#25D366]/30 bg-[#25D366]/5 p-4">
                  <p className="text-sm font-medium text-gray-900 dark:text-white">Ready to send?</p>
                  <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
                    Send <strong>{channel === 'rcs' ? rcsTemplateName : templateName}</strong> to{' '}
                    <strong>
                      {recipients.length} contact{recipients.length > 1 ? 's' : ''}
                    </strong>
                    . Check the preview on the right, then click Send above.
                  </p>
                  <button
                    type="button"
                    disabled={sending}
                    onClick={() => setStep('compose')}
                    className="mt-3 text-sm text-[#128C7E] hover:underline"
                  >
                    ← Edit message
                  </button>
                </div>
              )}

              {step === 'compose' && proceedHint && (
                <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:border-amber-900/40 dark:bg-amber-950/30 dark:text-amber-200">
                  {proceedHint}
                </p>
              )}

              <div>
                <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-gray-500">
                  1. Recipients
                </label>
                <WhatsAppSendRecipientPicker
                  recipients={recipients}
                  onChange={setRecipients}
                  disabled={sending}
                />
              </div>

              <div>
                <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-gray-500">
                  2. Template & variables
                </label>
                {channel === 'rcs' ? (
                  <div className="space-y-3">
                    {!rcsStatus?.enabled && (
                      <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:border-amber-900/40 dark:bg-amber-950/30 dark:text-amber-200">
                        RCS is not enabled on this backend. Connect RCS in Interakt, then set INTERAKT_RCS_ENABLED=true
                        and INTERAKT_RCS_TEMPLATE_NAME.
                      </p>
                    )}
                    {rcsStatus?.note && (
                      <p className="text-xs text-violet-800 dark:text-violet-200">{rcsStatus.note}</p>
                    )}
                    {(rcsStatus?.templates || []).length > 0 ? (
                      <select
                        value={rcsTemplateName}
                        onChange={(e) => handleRcsTemplateChange(e.target.value)}
                        className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800"
                      >
                        <option value="">Select RCS template…</option>
                        {(rcsStatus?.templates || []).map((t) => (
                          <option key={t.name} value={t.name}>
                            {t.displayName || t.name}
                          </option>
                        ))}
                      </select>
                    ) : null}
                    <input
                      value={rcsTemplateName}
                      onChange={(e) => handleRcsTemplateChange(e.target.value)}
                      placeholder="RCS template code name"
                      className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800"
                    />
                    {selectedRcsTemplate?.bodyPreview && (
                      <p className="whitespace-pre-wrap rounded-lg bg-gray-50 p-3 text-xs text-gray-700 dark:bg-gray-800 dark:text-gray-200">
                        {selectedRcsTemplate.bodyPreview}
                      </p>
                    )}
                    <div className="flex items-center justify-between">
                      <p className="text-xs font-medium text-gray-500">Body variables</p>
                      <button
                        type="button"
                        onClick={() => setRcsBodyValues((prev) => [...prev, ''])}
                        className="text-xs text-violet-700 hover:underline dark:text-violet-300"
                      >
                        Add variable
                      </button>
                    </div>
                    {rcsBodyValues.length === 0 && (
                      <p className="text-xs text-gray-500">No variables. Add one if this RCS template has {'{{1}}'} placeholders.</p>
                    )}
                    {rcsBodyValues.map((value, index) => (
                      <label key={`rcs-var-${index}`} className="block text-sm">
                        <span className="mb-1 block text-gray-600 dark:text-gray-300">
                          {'{{'}
                          {index + 1}
                          {'}}'}
                        </span>
                        <div className="flex gap-2">
                          <input
                            value={value}
                            onChange={(e) =>
                              setRcsBodyValues((prev) => prev.map((v, i) => (i === index ? e.target.value : v)))
                            }
                            className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800"
                          />
                          <button
                            type="button"
                            onClick={() => setRcsBodyValues((prev) => prev.filter((_, i) => i !== index))}
                            className="text-xs text-gray-500 hover:text-red-600"
                          >
                            Remove
                          </button>
                        </div>
                      </label>
                    ))}
                  </div>
                ) : (
                <div className="space-y-3">
                  <WhatsAppTemplateSearchSelect
                    templates={templates}
                    value={templateName}
                    onChange={handleTemplateChange}
                    placeholder="Search template…"
                    emptyLabel="Select approved template"
                  />

                  {selectedTemplate && variableSlots.length > 0 && (
                    <>
                      {selectedTemplate.defaultVariableMappings?.length ? (
                        <p className="text-xs text-[#128C7E]">
                          Loaded mappings from template defaults where they match recipient fields. Adjust below if
                          needed.
                        </p>
                      ) : null}
                      <WhatsAppSendVariableEditor
                        slots={variableSlots}
                        variableLabels={variableLabels}
                        recipients={recipients}
                        contextFields={sendContextFields}
                        campaigns={deepLinkCampaigns}
                        onCampaignCreated={(c) =>
                          setDeepLinkCampaigns((prev) =>
                            prev.some((x) => x.id === c.id) ? prev : [c, ...prev]
                          )
                        }
                        onChange={setVariableSlots}
                        disabled={sending}
                      />
                    </>
                  )}

                  {selectedTemplate && variableSlots.length === 0 && (
                    <p className="text-sm text-gray-500">This template has no body variables.</p>
                  )}

                  {selectedTemplate && (
                    <WhatsAppSendButtonLinkEditor
                      template={selectedTemplate}
                      slots={buttonSlots}
                      onChange={setButtonSlots}
                      campaigns={deepLinkCampaigns}
                      contextFields={sendContextFields}
                      onCampaignCreated={(c) =>
                        setDeepLinkCampaigns((prev) =>
                          prev.some((x) => x.id === c.id) ? prev : [c, ...prev]
                        )
                      }
                      disabled={sending}
                    />
                  )}

                  {selectedTemplate && supportsMediaHeader && (
                    <div>
                      <label className="mb-1 block text-xs font-medium text-gray-500">
                        Header {String(selectedTemplate.headerFormat || 'media').toLowerCase()} (optional)
                      </label>
                      <p className="mb-2 text-[11px] text-gray-500">
                        This template supports a {String(selectedTemplate.headerFormat || 'media').toLowerCase()}{' '}
                        header. {String(selectedTemplate.headerFormat || '').toUpperCase() === 'VIDEO' ? 'Upload a video (.mp4/.3gp, max 16MB).' : 'Upload a public file URL that Interakt can fetch.'}
                      </p>
                      <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-dashed border-gray-300 px-3 py-3 text-sm text-gray-600 hover:bg-gray-50 dark:border-gray-600 dark:hover:bg-gray-800">
                        <PaperClipIcon className="h-4 w-4" />
                        {uploadingAttachment
                          ? 'Uploading...'
                          : attachmentName
                            ? attachmentName
                            : String(selectedTemplate.headerFormat || '').toUpperCase() === 'VIDEO'
                              ? 'Choose video file (.mp4, .3gp — max 16MB)'
                              : String(selectedTemplate.headerFormat || '').toUpperCase() === 'IMAGE'
                                ? 'Choose image file (JPG, PNG — max 5MB)'
                                : 'Choose image, video, PDF, or document'}
                        <input
                          type="file"
                          accept={headerFormatAcceptAttribute(selectedTemplate.headerFormat)}
                          className="hidden"
                          disabled={uploadingAttachment}
                          onChange={(e) => handleAttachment(e.target.files?.[0] || null)}
                        />
                      </label>
                    </div>
                  )}

                  {selectedTemplate && !supportsMediaHeader && (
                    <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:border-amber-900/40 dark:bg-amber-950/30 dark:text-amber-200">
                      This template has no image/video/document header in Interakt, so attachments cannot be sent with
                      it. Create or pick a template with a media header to include files.
                    </p>
                  )}
                </div>
                )}
              </div>
            </div>

            <div className="flex min-h-0 flex-col overflow-y-auto bg-gray-50 p-4 sm:p-6 dark:bg-gray-950/40">
              <WhatsAppDevicePreview
                message={
                  channel === 'rcs'
                    ? selectedRcsTemplate?.bodyPreview || rcsTemplateName || 'Select an RCS template to preview'
                    : devicePreviewProps.message
                }
                headerText={devicePreviewProps.headerText}
                footer={devicePreviewProps.footer}
                buttons={devicePreviewProps.buttons}
                headerMediaUrl={devicePreviewProps.headerMediaUrl}
                headerMimeType={devicePreviewProps.headerMimeType}
                contactName={previewContactName}
              />

              <div className="mt-4 rounded-xl border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-900">
                <p className="text-xs font-medium text-gray-700 dark:text-gray-200">
                  Recipients ({recipients.length})
                </p>
                {recipients.length === 0 ? (
                  <p className="mt-2 text-xs text-gray-500">No contacts selected yet</p>
                ) : (
                  <ul className="mt-2 max-h-40 space-y-1.5 overflow-y-auto text-xs">
                    {recipients.map((r) => (
                      <li
                        key={r.key}
                        className="flex items-center justify-between gap-2 rounded-lg bg-gray-50 px-2 py-1.5 dark:bg-gray-800/80"
                      >
                        <span className="truncate font-medium text-gray-800 dark:text-gray-100">
                          {r.displayName}
                        </span>
                        <span className="shrink-0 text-gray-500 dark:text-[#8696a0]">
                          {formatDisplayPhone(r.countryCode, r.phoneNumber)}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {recipients.length > 1 && (
                <p className="mt-3 text-center text-[11px] text-gray-500">
                  Notification field mappings are resolved individually for each of the {recipients.length} contacts.
                </p>
              )}
            </div>
          </div>
        </div>
      </WhatsAppPanelShell>

      <ConfirmDialog
        open={!!sizeError}
        title="File too large"
        message={
          sizeError
            ? `"${sizeError.name}" is ${formatFileSize(sizeError.size)}. Maximum allowed is ${formatFileSize(sizeError.limit)} for this file type.`
            : ''
        }
        tone="warning"
        confirmLabel="OK"
        cancelLabel="Close"
        stackLevel="top"
        onCancel={() => setSizeError(null)}
        onConfirm={() => setSizeError(null)}
      />
    </>
  );
};

export default WhatsAppSendTemplatePanel;
